import { useState, Suspense, useEffect, useRef, useMemo } from "react";
import { Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { MdLogout } from "react-icons/md";
import { IoNotificationsOutline } from "react-icons/io5";
import { FiMoon } from "react-icons/fi";
import { BiSun } from "react-icons/bi";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Header } from "gf-ui-components-mui-lib";
import avatar from "@img/avatar.jpg";
import withNotification from "@hoc/withNotification";
import withAuth from "@hoc/withAuth";
import { BiHomeAlt } from "react-icons/bi";
import { MainComponent } from "./styles";
import sidebarLinks from "./sidebar.links";
import { NavLink } from "./sidebar.links";
import { modGetAllowedUrls } from "@utils/helpers";
import { PRODUCT_LOGOS } from "../constants/productLogos";
import { useProductList } from "../hooks/useProductList";
import useLogout from "@hooks/useLogout";
import {
  WorkspaceSidebar,
  SIDEBAR_WIDTH,
  WORKSPACE_NAME_EVES,
  WORKSPACE_ACCENT_EVES,
  normalizeNavUrl,
  ProductDropdownItem,
} from "./sidebar";
import { GroupSVG } from "@svg";

const collectNavUrls = (items: NavLink[]): string[] =>
  items.flatMap((item) => {
    const urls: string[] = [];
    if (item.url) {
      urls.push(normalizeNavUrl(item.url));
    }
    if (item.sublinks?.length) {
      urls.push(...collectNavUrls(item.sublinks));
    }
    return urls;
  });

function MainLayout() {
  const [themes, setTheme] = useState<any>("light");
  const [user, setUser] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [filteredLinks, setFilteredLinks] = useState<Array<NavLink>>([]);
  const [showProductDropdown, setShowProductDropdown] = useState<boolean>(false);
  const { products, hasSingleProduct } = useProductList();
  const { handleLogout } = useLogout();
  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const permissions = JSON.parse(localStorage.getItem("userscope") as string);
    if (permissions) {
      const mod = modGetAllowedUrls(permissions);
      const filterLinks = (links: NavLink[]): NavLink[] =>
        links.reduce<NavLink[]>((filtered, link) => {
          if (link.isSubLinks && link.sublinks) {
            const filteredSublinks = filterLinks(link.sublinks);
            if (filteredSublinks.length > 0) {
              filtered.push({ ...link, sublinks: filteredSublinks });
            }
          } else if (link.id && mod.has(link.id)) {
            filtered.push({ ...link });
          }
          return filtered;
        }, []);

      const defaultHomeLink: NavLink = {
        id: "$home",
        icon: <BiHomeAlt size={25} />,
        label: "Home",
        url: "/dashboard",
      };

      setFilteredLinks([
        defaultHomeLink,
        ...filterLinks(sidebarLinks._dashboardLinks),
      ]);
    }
  }, []);

  const selectedItem = useMemo(() => {
    const links = collectNavUrls(filteredLinks);
    const sortedLinks = [...links].sort((a, b) => b.length - a.length);
    return sortedLinks.find(
      (link) =>
        location.pathname === link || location.pathname.startsWith(`${link}/`)
    );
  }, [location.pathname, filteredLinks]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showProductDropdown &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showProductDropdown]);

  const toggleTheme = () => {
    const body = document.body;
    const theme = body.dataset.theme;
    if (theme === "dark") {
      body.dataset.theme = "light";
      setTheme("light");
    } else {
      body.dataset.theme = "dark";
      setTheme("dark");
    }
  };

  const handleProductSelect = (item: ProductDropdownItem) => {
    window.open(item?.link, "_self");
    setShowProductDropdown(false);
  };

  const changeTheProducts = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (products?.length > 0) {
      setShowProductDropdown(!showProductDropdown);
    } else {
      navigate("/dashboard");
    }
  };

  const handleNavSelect = (url: string) => {
    navigate(url);
  };

  const controls_data = [
    {
      id: "theme",
      label: "Theme",
      icon: themes === "light" ? <FiMoon /> : <BiSun />,
      handleControl: () => toggleTheme(),
    },
    {
      id: "notification",
      label: "Notification",
      icon: <IoNotificationsOutline />,
    },
  ];

  const menuitems = [
    {
      id: "logout",
      label: hasSingleProduct ? "Logout" : "Exit",
      icon: <MdLogout />,
      handleItem: () =>
        handleLogout({ redirectToLogoutEndpoint: hasSingleProduct }),
    },
  ];

  const isAuthenticated = () => Boolean(localStorage.getItem("access_token"));

  useEffect(() => {
    setUser((localStorage.getItem("username") as string) || "");
  }, []);

  const theme = useTheme();
  const displayName = user || "User";

  return (
    <>
      <Box>
        {isAuthenticated() ? (
          <Box>
            <Header
              position="fixed"
              color="primary"
              searchBar={false}
              controls={controls_data}
              user={displayName}
              menuItems={menuitems}
              avatarImg={avatar}
              sx={{
                boxShadow: "none",
                zIndex: "1200",
                marginLeft: `${SIDEBAR_WIDTH}px`,
                width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
                maxWidth: `calc(100% - ${SIDEBAR_WIDTH}px)`,
                boxSizing: "border-box",
              }}
            />
            <div ref={sidebarRef}>
              <WorkspaceSidebar
                navItems={filteredLinks}
                selectedItem={selectedItem}
                onSelect={handleNavSelect}
                workspaceName={WORKSPACE_NAME_EVES}
                workspaceIconSrc={PRODUCT_LOGOS.evesOps}
                workspaceAccentColor={WORKSPACE_ACCENT_EVES}
                showProductSwitcher={products.length > 0}
                groupIcon={<GroupSVG />}
                isProductDropdownOpen={showProductDropdown}
                productItems={products}
                onProductSwitcherClick={changeTheProducts}
                onProductSelect={handleProductSelect}
                userName={displayName}
                userRole="User"
              />
            </div>
            <MainComponent theme={theme}>
              <Suspense fallback={<>Loading...</>}>
                <Outlet />
              </Suspense>
            </MainComponent>
          </Box>
        ) : (
          <Navigate to="/" replace />
        )}
      </Box>
    </>
  );
}

// Wrap authenticated layout with withAuth so tokens refresh silently before expiry
export default withAuth(withNotification(MainLayout));
